import {
    productQuantity,
    UNIT_MEASURE_CODE,
    type ProductQuantityRules,
} from "@ecommerce/shared-utils";
import type { Product } from "../types/Product";
import type { ProductVariant } from "../types/ProductVariant";

type QuantityProduct = Pick<
    Product,
    "id" | "stock" | "stockAlmacen" | "tinCodUnidadMedida" | "unidadMedida" | "cantDecimal"
>;

type QuantityVariant = Pick<ProductVariant, "id" | "stock" | "stockAlmacen">;

export interface VodaProductQuantityContext {
    unitMeasureCode: number;
    unitLabel: string;
    cantDecimal: number;
    minimum: number;
    initialQuantity: number;
    stock: number | null;
    isPurchasable: boolean;
    rules: ProductQuantityRules;
}

const missingCanonicalStockReports = new Set<string>();

const reportMissingCanonicalStock = (
    product: QuantityProduct,
    variant: QuantityVariant | null | undefined,
    usedLegacyStock: boolean,
) => {
    const key = `${product.id}:${variant?.id ?? "product"}`;
    if (missingCanonicalStockReports.has(key)) return;

    missingCanonicalStockReports.add(key);
    console.error("[cantidad] Falta stockAlmacen para un flujo migrado", {
        productId: product.id,
        variantId: variant?.id,
        policy: usedLegacyStock ? "legacy-unit-stock" : "blocked-without-canonical-stock",
    });
};

export const resolveVodaProductQuantityContext = (
    product: QuantityProduct,
    variant: QuantityVariant | null | undefined,
    storefrontMinimum: number | null | undefined,
): VodaProductQuantityContext => {
    const unitMeasureCode = product.tinCodUnidadMedida ?? UNIT_MEASURE_CODE;
    const cantDecimal =
        unitMeasureCode === UNIT_MEASURE_CODE
            ? 0
            : productQuantity.resolvePrecision(product.cantDecimal);
    const stockResolution = productQuantity.resolveStock({
        canonicalStock: variant ? variant.stockAlmacen : product.stockAlmacen,
        legacyStock: variant ? variant.stock : product.stock,
        unitMeasureCode,
        // La tienda Voda nunca habilita sobreventa; ese permiso es interno.
        allowWithoutStock: false,
    });

    if (stockResolution.missingCanonicalStock) {
        reportMissingCanonicalStock(product, variant, stockResolution.usedLegacyStock);
    }

    const stock =
        stockResolution.stock == null
            ? 0
            : (productQuantity.getMaximumSelectableStock(stockResolution.stock, cantDecimal) ?? 0);
    const rules: ProductQuantityRules = {
        cantDecimal,
        storefrontMinimum,
        stock,
    };
    const minimum = productQuantity.getMinimum(rules);
    const initialQuantity = productQuantity.getInitialQuantity(rules);

    return {
        unitMeasureCode,
        unitLabel:
            product.unidadMedida?.trim() ||
            (unitMeasureCode === UNIT_MEASURE_CODE ? "Unidades" : "Medida"),
        cantDecimal,
        minimum,
        initialQuantity,
        stock,
        isPurchasable: stock >= minimum,
        rules,
    };
};
